import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, CloudOff, Clock, AlertTriangle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useNetwork } from '../../contexts/NetworkContext';

export const ConnectionStatusBanner: React.FC = () => {
  const { state, pendingOperationsCount, lastSyncTime, retryFailedOperations, checkConnection } = useNetwork();
  const [isRetrying, setIsRetrying] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (state.isOnline) {
      setDismissed(false);
    }
  }, [state.isOnline]);

  if (state.isOnline && pendingOperationsCount === 0) {
    return null;
  }

  if (dismissed && state.isOnline) {
    return null;
  }

  const handleRetry = async () => {
    setIsRetrying(true);
    await checkConnection();
    if (pendingOperationsCount > 0) {
      await retryFailedOperations();
    }
    setIsRetrying(false);
  };

  const formatLastSync = () => {
    if (!lastSyncTime) return 'Never';
    const diff = Date.now() - lastSyncTime.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return lastSyncTime.toLocaleDateString();
  };

  const isOffline = !state.isOnline;
  const hasPendingOps = pendingOperationsCount > 0;

  return (
    <div
      className={`relative transition-all duration-300 ${
        isOffline
          ? 'bg-gradient-to-r from-red-600 to-red-700'
          : 'bg-gradient-to-r from-amber-500 to-amber-600'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isOffline ? 'bg-red-500/30' : 'bg-amber-400/30'}`}>
              {isOffline ? (
                <WifiOff className="h-5 w-5 text-white" />
              ) : (
                <CloudOff className="h-5 w-5 text-white" />
              )}
            </div>

            <div className="text-white">
              <div className="font-semibold flex items-center gap-2">
                {isOffline ? (
                  <>
                    <span>You are currently offline</span>
                    {state.isReconnecting && (
                      <span className="text-sm font-normal opacity-80">(reconnecting...)</span>
                    )}
                  </>
                ) : (
                  <span>Pending changes to sync</span>
                )}
              </div>
              <div className="text-sm opacity-90">
                {isOffline
                  ? 'Some features may be limited. Your changes will be saved locally.'
                  : `${pendingOperationsCount} operation${pendingOperationsCount !== 1 ? 's' : ''} waiting to sync`}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white"
            >
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-white font-medium disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{isRetrying ? 'Retrying...' : 'Retry'}</span>
            </button>

            {!isOffline && (
              <button
                onClick={() => setDismissed(true)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {showDetails && (
          <div className="mt-3 pt-3 border-t border-white/20">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-white text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 opacity-70" />
                <span className="opacity-70">Last sync:</span>
                <span className="font-medium">{formatLastSync()}</span>
              </div>

              <div className="flex items-center gap-2">
                <CloudOff className="h-4 w-4 opacity-70" />
                <span className="opacity-70">Pending:</span>
                <span className="font-medium">{pendingOperationsCount} operations</span>
              </div>

              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 opacity-70" />
                <span className="opacity-70">Connection:</span>
                <span className="font-medium capitalize">{state.quality}</span>
              </div>
            </div>

            {isOffline && (
              <div className="mt-3 p-3 rounded-lg bg-white/10 text-white text-sm">
                <p className="font-medium mb-1">While offline you can:</p>
                <ul className="list-disc list-inside space-y-1 opacity-90">
                  <li>View cached inventory data</li>
                  <li>Create new entries (will sync when online)</li>
                  <li>Edit existing items (changes saved locally)</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
